/**
 * The reading path: the pre-built progressions as one journey.
 *
 * Four tracks, each made of engine configs that ship here, so nothing on the
 * path needs AI: Blending (the blend engine climbs its own five-level
 * ladder), Word families, Sight words in small sets in the order the little
 * books need them, and Little books unlocked by her blending level. Where she
 * stands on each step comes from her item memory (the truth across every
 * game) and the step's own carry (what the engine saved last time), in words
 * a parent reads at a glance.
 *
 * Blending has two numbers. The level she holds, from the words her memory
 * shows she reads, is where she is: it alone opens families and books, and
 * it is what the parent reads. The rung the game plays at (its carry) is how
 * hard today's words are, at most one above the level she holds: it climbs
 * only once she holds the rung she is on, so a lucky streak in one sitting
 * cannot carry her past what she reads.
 *
 * A parent can place her: "she reads at level 2" starts the game there and
 * counts the levels below it as held, so what they gate opens, because the
 * parent sees her read. The placement is its own record beside her play
 * (never words marked read), shown as placed by the parent until her play
 * reaches it or her misses since say otherwise, and taking it back leaves
 * exactly what her play shows.
 *
 * A level, a sight set or a book she has reached stays reached while she is
 * away (holds): only a miss takes a word back. What fades with time is what
 * the games bring back for review, and the path says a level has gone a
 * little rusty rather than taking it from her.
 */

import { holds, itemKey, knowsOnSight, openSets, type ItemMemory } from '../memory'
import { engineMaterialHtml, engineProgress } from './engines'
import { BOOK_READS, blendedWords, blendLevelFromMemory, blendLevelStanding, decodableLevel, GRAPHEMES, LEVELS, SIGHT_WORDS, splitGraphemes, WORD_BY_TEXT, wordFamilies } from './phonics'
import { bookSightWords, LITTLE_BOOKS, SIGHT_SETS, SOUND_SETS } from './books'
import { soundEvidence, wordItems } from './deckStats'
import type { Deck } from './decks'

export type PathTrackId = 'sounds' | 'blend' | 'families' | 'sight' | 'books'

export interface PathStep {
  /** Stable across releases: a child's saved progress hangs off it. */
  id: string
  track: PathTrackId
  title: string
  /** For the parent: what this step is. */
  blurb: string
  engine: string
  config: Record<string, unknown>
  /** A word-bank word (drawn as its clay picture) or an emoji. */
  cover?: string
}

export interface PathTrack {
  id: PathTrackId
  title: string
  blurb: string
  steps: PathStep[]
}

/**
 * Families open once she holds level 1: changing the first sound of a word
 * she reads (mat, rat) is the easiest bridge into blending. The families
 * engine offers only families whose words she can read at the level she
 * holds, so held first sounds come first and quick ones (cat, hat) join at
 * level 2.
 */
const FAMILIES_LEVEL = 1

/** Words as a parent would say them: "the, a, is and no". Also what a screen reader hears for a set. */
export const andList = (words: string[]) => (words.length < 2 ? words.join('') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`)
const list = andList

export const READING_PATH: PathTrack[] = [
  {
    id: 'sounds',
    title: 'Sounds',
    blurb: 'The sound each letter makes (mmm, not em), a few at a time: what she joins up when she blends.',
    steps: SOUND_SETS.map(({ letters, level }, i) => ({
      id: `sounds-${i + 1}`, track: 'sounds' as const, title: letters.join(' · '),
      blurb: `Hearing the sounds of ${list(letters)} and finding each letter, then the picture that starts with it. For blending level ${level}.`,
      engine: 'sight', config: { letters }, cover: '👂',
    })),
  },
  {
    id: 'blend',
    title: 'Blending',
    blurb: 'Sounding out a word and joining the sounds into it: the heart of reading.',
    steps: [{
      id: 'blend', track: 'blend', title: 'Blending',
      blurb: `She slides a ladybug under a word and the sounds join up. It climbs five levels on its own, from ${LEVELS[0]!.name.toLowerCase()} to ${LEVELS[LEVELS.length - 1]!.name.toLowerCase()}.`,
      engine: 'blend', config: { level: 1 }, cover: '🐞',
    }],
  },
  {
    id: 'families',
    title: 'Word families',
    blurb: 'Words that rhyme because they end the same (cat, hat, bat): reading by chunks.',
    steps: [{
      id: 'families', track: 'families', title: 'Word families',
      blurb: 'She changes the first sound of a word she reads and hears the family rhyme (man, fan, van). It picks the families she has met least, from words at her blending level.',
      engine: 'families', config: {}, cover: 'fan',
    }],
  },
  {
    id: 'sight',
    title: 'Sight words',
    blurb: 'Words her books need that she cannot sound out yet (said, go, see), a few at a time by sight.',
    steps: SIGHT_SETS.map((words, i) => ({
      id: `sight-${i + 1}`, track: 'sight' as const, title: words.join(' · '),
      blurb: `Finding ${list(words)} among words that look alike.`,
      engine: 'sight', config: { words }, cover: '👀',
    })),
  },
  {
    id: 'books',
    title: 'Little books',
    blurb: 'Short stories she reads herself, every word one she can sound out or knows by sight.',
    steps: LITTLE_BOOKS.map((b) => ({
      id: `book-${b.id}`, track: 'books' as const, title: b.config.title,
      blurb: `A ${b.config.pages.length}-page book for blending level ${b.config.level}. Reading it again and again is how it gets smooth.`,
      engine: 'story', config: b.config as unknown as Record<string, unknown>, cover: b.config.cover,
    })),
  },
]

const STEPS: Record<string, PathStep> = Object.fromEntries(READING_PATH.flatMap((t) => t.steps).map((s) => [s.id, s]))

/** A step by its id, or null for one this build does not have. */
export function pathStep(id: string): PathStep | null {
  return STEPS[id] ?? null
}

/**
 * A step as it is for her: the step as this build ships it, with the setup
 * changed for her on top (a book rewritten without the pig she is scared
 * of, a chosen blending level). A setup that retitles or recovers the step
 * (a book's title and cover) renames it everywhere the step shows, open or
 * locked, so a change made for her is seen at once.
 */
export function ownStep(step: PathStep, config: unknown): PathStep {
  const own = config && typeof config === 'object' ? (config as Record<string, unknown>) : {}
  if (!Object.keys(own).length) return step
  const text = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined)
  const base = step.engine === 'story' ? ownBook(step.config, own) : { ...step.config, ...own }
  return { ...step, config: base, title: text(base.title) ?? step.title, cover: text(base.cover) ?? step.cover }
}

type BookPage = { text: string; scene: string[]; [k: string]: unknown }

/**
 * A book changed for her is kept as the change, laid over the book as this
 * build ships it, so every later improvement to the story still reaches
 * her: `swap` trades words everywhere they appear (pig to hen: the title,
 * the cover, every line and every picture), and `pages` keyed by page
 * number ({"3": page}) replaces only those pages. A whole pages list is a
 * different story, and is kept whole. Title, cover, names and listener set
 * outright win over the swap.
 */
function ownBook(shipped: Record<string, unknown>, own: Record<string, unknown>): Record<string, unknown> {
  const { swap, pages, ...rest } = own
  const book = swapBook(shipped, swap)
  const edited = Array.isArray(pages)
    ? pages
    : pages && typeof pages === 'object'
      ? (book.pages as BookPage[]).map((p, i) => ((pages as Record<string, unknown>)[String(i + 1)] as BookPage | undefined) ?? p)
      : book.pages
  return { ...book, ...rest, pages: edited }
}

/** Words traded in a book (pig to hen), whole words only, keeping a capital and a plural. */
export function swapBook(book: Record<string, unknown>, swap: unknown): Record<string, unknown> {
  const pairs = swap && typeof swap === 'object' ? Object.entries(swap as Record<string, unknown>).filter((e): e is [string, string] => typeof e[1] === 'string' && !!e[0].trim() && !!e[1].trim()) : []
  if (!pairs.length) return book
  const word = (w: string) => pairs.find(([from]) => from.toLowerCase() === w.toLowerCase())?.[1] ?? w
  const line = (t: string) => pairs.reduce((out, [from, to]) => out.replace(new RegExp(`\\b${from.replace(/[^A-Za-z']/g, '')}(s?)\\b`, 'gi'), (m: string, s: string) => (/^[A-Z]/.test(m) ? to[0]!.toUpperCase() + to.slice(1) : to) + s), t)
  const pages = Array.isArray(book.pages) ? (book.pages as BookPage[]) : []
  return {
    ...book,
    ...(typeof book.title === 'string' ? { title: line(book.title) } : {}),
    ...(typeof book.cover === 'string' ? { cover: word(book.cover) } : {}),
    ...(Array.isArray(book.names) ? { names: (book.names as string[]).map(word) } : {}),
    pages: pages.map((p) => ({ ...p, text: line(p.text), scene: p.scene.map(word) })),
  }
}

/**
 * A rewrite of a book as the change it makes, against the book as it
 * ships: the pages that differ, by number, when it keeps the book's shape;
 * the whole list when it is a different story. What ownBook lays back on.
 */
export function bookChange(shipped: Record<string, unknown>, own: Record<string, unknown>): Record<string, unknown> {
  if (!Array.isArray(own.pages)) return own
  const base = swapBook(shipped, own.swap).pages as BookPage[]
  const pages = own.pages as BookPage[]
  if (pages.length !== base.length) return own
  const changed = Object.fromEntries(pages.flatMap((p, i) => (JSON.stringify(p) === JSON.stringify(base[i]) ? [] : [[String(i + 1), p]])))
  const { pages: _whole, ...rest } = own
  return Object.keys(changed).length ? { ...rest, pages: changed } : rest
}

/** A reading-path row's step as it is for her (ownStep), or null for a row that is not a path step. */
export function rowStep(row: { data?: { path?: unknown; config?: unknown } | null } | null | undefined): PathStep | null {
  const step = typeof row?.data?.path === 'string' ? pathStep(row.data.path) : null
  return step ? ownStep(step, row!.data!.config) : null
}

/**
 * What a reading-path row plays: its step as it is for her (rowStep). A
 * row's stored html is only the step as it stood when she first opened it,
 * so every host and the agent read a step through this, and a book
 * rewritten since reaches her the next time she plays it. Undefined for a
 * row that is not a path step.
 */
export function pathStepHtml(row: { data?: { path?: unknown; config?: unknown } | null } | null | undefined): string | undefined {
  const step = rowStep(row)
  return step ? engineMaterialHtml(step.engine, step.config) : undefined
}

export type StepState = 'locked' | 'open' | 'playing' | 'done'

export interface StepStanding {
  step: PathStep
  state: StepState
  /** Where she is, for the parent ("Playing level 3 of 5: words like ship · 2 more words read well open level 4"). Null before she starts. */
  progress: string | null
  /** 0 to 1, for a progress bar. */
  fraction: number
  /** Sets only (sounds, sight words): the members she knows, the ones its progress line names. */
  known?: string[]
  /** For a locked step: what opens it, in a whole sentence. */
  unlockHint?: string
  /** For a locked step: what opens it, as data, so no app parses the sentence (unlockPhrase says it short). */
  unlock?: StepUnlock
  /**
   * Blending only. `level` is the level she holds (0 before any), which opens
   * families and books; `rung` is the level the game plays at today, which
   * can run ahead of it. A ladder marks them apart.
   */
  level?: number
  rung?: number
  /**
   * Blending only: the level the parent placed her at, while her own play
   * has not reached it. `holds` is the level it counts as held (the one
   * below); `contradicted` when her misses since the placement say she does
   * not read those words yet, and the placement then holds nothing.
   */
  placed?: { level: number; holds: number; contradicted?: boolean }
  /**
   * Blending only: how near she is to the next level, the same count every
   * step waiting on that level names, so the ladder, its line and the locks
   * never give two numbers for one thing.
   */
  toward?: { level: number; held: number; needed: number; pending?: number }
}

/**
 * What a locked step waits on: blending words of a level she has yet to
 * read well, sight words she has not met, or the sight set before it.
 */
export type StepUnlock =
  | { kind: 'level'; level: number; need: number; have: number }
  | { kind: 'meet'; words: string[] }
  | { kind: 'set'; after: string[] }
  /** Every condition at once (a book waiting on a level and on sight words), so nothing it waits on goes unsaid. */
  | { kind: 'all'; parts: StepUnlock[] }

type LevelUnlock = Extract<StepUnlock, { kind: 'level' }>

/** A few words by name, the rest counted: "he, she, me and 3 more". */
const few = (words: string[], n = 3) => (words.length > n + 1 ? `${words.slice(0, n).join(', ')} and ${words.length - n} more` : list(words))

/** What opens a locked step in a few words, for under a small cover: "2 more level 1 words", "After meeting said", "1 more level 1 word and meeting to". */
export function unlockPhrase(u: StepUnlock | undefined): string {
  if (!u) return 'Not yet'
  if (u.kind === 'all') return u.parts.map((p, i) => (p.kind === 'meet' && i ? `meeting ${few(p.words)}` : unlockPhrase(p))).join(' and ')
  if (u.kind === 'level') {
    const left = Math.max(1, u.need - u.have)
    return `${left} more level ${u.level} ${left === 1 ? 'word' : 'words'}`
  }
  if (u.kind === 'meet') return `After meeting ${few(u.words)}`
  return `After ${list(u.after)}`
}

/**
 * What opens a book, or a stack of them: the blending level it waits on and
 * every sight word she has yet to meet, so the shelf never promises a book
 * the next word will not open.
 */
function bookLock(level: LevelUnlock | undefined, words: string[]): Pick<StepStanding, 'unlockHint' | 'unlock'> {
  const meet = words.length ? { kind: 'meet' as const, words } : undefined
  const parts: StepUnlock[] = [level, meet].filter((p): p is NonNullable<typeof p> => !!p)
  const unlockHint = level && meet
    ? `${levelHint(level)} and meets ${few(words)} in Sight words`
    : level ? levelHint(level) : `Opens when she has met ${few(words)} in Sight words`
  return { unlockHint, unlock: parts.length > 1 ? { kind: 'all', parts } : parts[0] }
}

/** A locked book's conditions, apart: the level it waits on and the sight words. */
function bookParts(u: StepUnlock | undefined): { level?: LevelUnlock; words: string[] } {
  const parts = !u ? [] : u.kind === 'all' ? u.parts : [u]
  return {
    level: parts.find((p): p is LevelUnlock => p.kind === 'level'),
    words: parts.flatMap((p) => (p.kind === 'meet' ? p.words : [])),
  }
}

/**
 * Little books as a shelf shows them, the same in both apps: the books she
 * can read one by one, and the ones still ahead in one stack per level and
 * per reason they wait ("The Big Pig and 2 more"), so the shelf reads as
 * what she has and what is coming, not a wall of padlocks.
 */
export function bookShelf(steps: StepStanding[]): {
  open: StepStanding[]
  ahead: Array<{ level: number; books: StepStanding[]; unlockHint?: string; unlock?: StepUnlock }>
} {
  // One stack per level and per whether it waits on that level; the stack
  // names every sight word any of its books still needs.
  const ahead = new Map<string, StepStanding[]>()
  for (const s of steps) {
    if (s.state !== 'locked') continue
    const key = `${s.step.config.level}|${bookParts(s.unlock).level ? 'level' : 'words'}`
    ahead.set(key, [...(ahead.get(key) ?? []), s])
  }
  return {
    open: steps.filter((s) => s.state !== 'locked'),
    ahead: [...ahead.values()].map((books) => {
      const parts = books.map((b) => bookParts(b.unlock))
      return { level: Number(books[0]!.step.config.level), books, ...bookLock(parts[0]!.level, [...new Set(parts.flatMap((p) => p.words))]) }
    }),
  }
}

export interface PathInput {
  memory: Record<string, ItemMemory>
  /**
   * Each step's row for her: its carry, the setup changed for her (a chosen
   * blending level, a book rewritten for her), whether it is taken off her
   * path, and when it last changed.
   */
  played: Record<string, PlayedStep>
  now: number
  /**
   * Her decks, so a sight word she reads on a card counts on the path the
   * way it counts in What she knows. Without them, the words she read in
   * games and books still count.
   */
  decks?: Deck[]
}

/** One step's row for her, as pathStanding reads it. */
export interface PlayedStep {
  carry?: unknown
  artifactId?: string
  config?: Record<string, unknown>
  /** Taken off her path by the parent (a book about something she is scared of): it is not shown or offered. */
  hidden?: boolean
  /** When the row last changed (her last play saves its carry). */
  at?: number
  /** Blending only: the level the parent placed her at, and when (see placement). */
  placed?: Placement
}

/** A parent's "she reads at level N": its own reversible record on her Blending row. */
export interface Placement {
  level: number
  at: number
}

/**
 * What a placement holds now. It counts the levels below the placed one as
 * held until her own play reaches them (then it says nothing she has not
 * shown), and stops holding anything once her play since contradicts it:
 * she has missed words of those levels more often than she read them, at
 * least twice. One miss is ordinary practice, not evidence against the parent.
 */
export function placement(memory: Record<string, ItemMemory>, placed: Placement | undefined, now: number): StepStanding['placed'] | undefined {
  if (!placed || !Number.isInteger(placed.level) || placed.level < 1) return undefined
  const holds = Math.min(LEVELS.length, placed.level) - 1
  if (holds < 1 || blendLevelFromMemory(memory, now) >= holds) return undefined
  const since = blendedWords(memory).filter((w) => w.level <= holds && w.memory.last >= placed.at)
  const missed = since.filter((w) => w.memory.streak === 0).length
  return { level: placed.level, holds, ...(missed >= 2 && missed > since.length - missed ? { contradicted: true } : {}) }
}

export interface PathStanding {
  tracks: Array<{ track: PathTrack; steps: StepStanding[] }>
  /** The one step to hand her next. */
  next: StepStanding | null
  /** Why that step is next, in one line for the parent ("Sounding out comes first while she finds level 1"). */
  why: string | null
}

/** What a step waiting on blending level n waits for, as data. The level rule is blendLevelFromMemory (phonics). */
function levelUnlock(memory: Record<string, ItemMemory>, n: number, now: number): LevelUnlock {
  const { held, needed } = blendLevelStanding(memory, n, now)
  return { kind: 'level', level: n, need: needed, have: held }
}

/** The sentence for a step waiting on a blending level. Any game, book or card she reads a word in counts, so it names no one game. */
const levelHint = (u: LevelUnlock) => `Opens once she reads ${u.need} level ${u.level} words well${u.have ? ` (${u.have} so far)` : ''}`

/**
 * Every memory that shows one sound of hers: the sound itself on a card or
 * in a game, and the words with it in them that she reads well (reading
 * cat proves k, a and t). A word she has only met proves nothing yet.
 */
function soundMemories(bySound: Record<string, string[]>, memory: Record<string, ItemMemory>, now: number) {
  return (letter: string): ItemMemory[] => (bySound[letter] ?? []).flatMap((k) => {
    const m = memory[k]
    return m && m.reps > 0 && (!k.startsWith('word:') || holds(m, now)) ? [m] : []
  })
}

/**
 * Whether she can sound out each word, by the rules the path itself keeps:
 * a sight word is learned whole; a word she reads well is hers; otherwise
 * it needs its spelling's blending level within what she plays, and every
 * sound in it shown the way the Sounds track counts them. For the agent and
 * anything else that speaks of what she can read, so no one judges it from
 * a word's letters (fish is f, i, sh).
 */
export function canSoundOut(words: string[], input: PathInput): Array<{ word: string; sounds: string[] | null; level: number | null; can: boolean; why: string }> {
  const { memory, now } = input
  const blend = pathStanding(input).tracks.find((t) => t.track.id === 'blend')!.steps[0]!
  const plays = blend.rung ?? 1
  const shown = soundMemories(soundEvidence(input.decks ?? [], memory), memory, now)
  const knowsSound = (g: string) => shown(g).some((m) => knowsOnSight(m, now))
    || Object.keys(GRAPHEMES).some((o) => o !== g && GRAPHEMES[o] === GRAPHEMES[g] && shown(o).some((m) => knowsOnSight(m, now)))
  return words.map((raw) => {
    const word = raw.trim().toLowerCase()
    const sounds = WORD_BY_TEXT[word]?.g ?? splitGraphemes(word)
    const level = decodableLevel(word)
    const out = (can: boolean, why: string) => ({ word, sounds, level, can, why })
    if (SIGHT_WORDS.some((w) => w.toLowerCase() === word)) return out(false, 'a sight word: learned whole, never sounded out')
    const mine = memory[itemKey('word', word)]
    if (mine && holds(mine, now)) return out(true, 'she reads it well')
    if (level === null) return out(false, 'spelled in a way the reading path does not teach yet')
    if (level > plays) return out(false, `a level ${level} word, above the level she plays (${plays})`)
    const unknown = [...new Set((sounds ?? []).filter((g) => !knowsSound(g)))]
    return unknown.length
      ? out(false, `at her level, but she has not shown ${andList(unknown)} yet`)
      : out(true, 'at her level, and she has shown every sound in it')
  })
}

/** Every memory she has of one word, from any game, book or card. */
type Evidence = (word: string) => ItemMemory[]

const carryOf = (played: PathInput['played'], id: string): Record<string, any> | null => {
  const c = played[id]?.carry
  return c && typeof c === 'object' ? (c as Record<string, any>) : null
}

/**
 * What holding level `toward` does, said from the rung she plays: on that
 * rung it opens the one above (or finishes the ladder); below it, holding
 * the next level moves her up to it; on a rung a parent chose above it, the
 * level becomes hers, which is what opens families and books.
 */
function levelReach(toward: number, rung: number, one = false): string {
  const verb = (base: string) => (one ? `${base}${base.endsWith('sh') ? 'es' : 's'}` : base)
  if (rung === toward) return toward < LEVELS.length ? `${verb('open')} level ${toward + 1}` : `${verb('finish')} the ladder`
  return rung < toward ? `${verb('move')} her up to level ${toward}` : `${verb('make')} level ${toward} hers`
}

/** Where she stands on every step of the path, and the step to hand her next. */
export function pathStanding(input: PathInput): PathStanding {
  const { memory, played, now } = input
  // Every step as it is for her: her own setup on top of the shipped one.
  const mine = (step: PathStep) => ownStep(step, played[step.id]?.config)
  const trackSteps = (id: PathTrackId) => READING_PATH.find((t) => t.id === id)!.steps.map(mine)
  const progressOf = (step: PathStep) => engineProgress(engineMaterialHtml(step.engine, step.config), carryOf(played, step.id), memory)
  const wasPlayed = (id: string) => !!played[id]?.carry

  // Blending: the level she holds opens things; the rung she plays at is
  // only how hard her words are today.
  const heldInPlay = blendLevelFromMemory(memory, now)
  const placed = placement(memory, played.blend?.placed, now)
  const level = placed && !placed.contradicted ? Math.max(heldInPlay, placed.holds) : heldInPlay
  const rusty = blendLevelFromMemory(memory, now, true) < heldInPlay
  const blendCarry = carryOf(played, 'blend')
  const saved = Number(blendCarry?.level)
  const chosen = Number(played.blend?.config?.level)
  // The same rung the engine starts on: its saved rung, never more than one
  // above the level she holds, or the level she holds when nothing is saved;
  // never below a level the parent chose.
  const auto = Number.isInteger(saved) && saved > 0 ? Math.min(saved, level + 1) : level
  const rung = Math.min(LEVELS.length, Math.max(1, auto, Number.isInteger(chosen) ? chosen : 0))
  const levelName = (n: number) => LEVELS.find((l) => l.n === n)!.name.toLowerCase()
  const toward = level < LEVELS.length ? { level: level + 1, ...blendLevelStanding(memory, level + 1, now) } : undefined
  // The line says what the ladder shows, in its order: the rung she plays
  // (the ringed one) and what reading its words well does next, by the same
  // count every step waiting on that level names. Words read right once come
  // after it, quietly: they count only when she reads them again on another
  // day, so they never sit beside the count as if she were already past it.
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`
  const left = toward ? Math.max(0, toward.needed - toward.held) : 0
  const ownLevel = placed && !placed.contradicted && rung === placed.level ? ' (placed by you)' : rung === chosen && rung > auto ? ' (chosen by you)' : ''
  const blendProgress = level || placed || wasPlayed('blend')
    ? [
        !toward ? `Reads all ${LEVELS.length} levels well`
          : `Playing level ${rung} of ${LEVELS.length}${ownLevel}: ${levelName(rung)}`,
        toward && left ? `${rung < toward.level ? 'she reads it well; ' : ''}${rung === toward.level ? plural(left, 'more word', 'more words') : plural(left, `level ${toward.level} word`, `level ${toward.level} words`)} read well ${levelReach(toward.level, rung, left === 1)}` : '',
        // Right once is on its way: a parent who watched her should see it, as what it is.
        toward?.pending && left ? `${toward.pending} read right once ${toward.pending === 1 ? 'counts when she reads it' : 'count when she reads them'} again another day` : '',
        placed?.contradicted ? `placed at level ${placed.level} by you, and her play since says not yet` : '',
        rusty ? 'a little rusty, a quick play brings it back' : '',
      ].filter(Boolean).join(' · ')
    : null
  const blend: StepStanding = {
    step: mine(STEPS.blend!),
    state: level === LEVELS.length ? 'done' : wasPlayed('blend') ? 'playing' : 'open',
    progress: blendProgress,
    fraction: toward ? Math.min(1, (level + Math.min(1, toward.held / toward.needed)) / LEVELS.length) : 1,
    level,
    rung,
    ...(placed ? { placed } : {}),
    ...(toward ? { toward: { level: toward.level, held: Math.min(toward.held, toward.needed), needed: toward.needed, pending: toward.pending } } : {}),
  }

  // Word families.
  const familyCount = Object.keys(wordFamilies()).length
  const met = Object.keys(carryOf(played, 'families')?.met ?? {}).length
  const familyStep = mine(STEPS.families!)
  const families: StepStanding = level < FAMILIES_LEVEL
    ? { step: familyStep, state: 'locked', progress: null, fraction: 0, ...bookLock(levelUnlock(memory, FAMILIES_LEVEL, now), []) }
    : {
        step: familyStep,
        state: met >= familyCount ? 'done' : wasPlayed('families') ? 'playing' : 'open',
        progress: progressOf(familyStep),
        fraction: Math.min(1, met / familyCount),
      }

  // Sight words: each set opens when she knows half the set before it on sight.
  // A word counts wherever she read it: a sight game, a book, a card.
  const byWord = wordItems(memory, input.decks ?? [])
  const evidence: Evidence = (w) => (byWord.get(w.toLowerCase()) ?? []).map((k) => memory[k]!)
  const sight = setSteps(trackSteps('sight'), (step) => (step.config.words as string[]) ?? [], evidence, {
    knows: (known) => `Knows ${list(known)} on sight`,
    lock: (_, prev) => ({ unlockHint: `Opens when she knows half of ${list(prev)} on sight`, unlock: { kind: 'set', after: prev } }),
  }, now, wasPlayed)
  // Sounds: the same, from what she shows of each sound in the letters game,
  // in Blending (which records every sound she slides through) and on her
  // cards. A word she reads well proves every sound in it (reading cat is k,
  // a and t), so the sounds, Blending and the books she opens tell one
  // story; a word she has only met proves nothing yet. Her decks count by
  // the same list (soundEvidence).
  //
  // A sound set is ready by the blending level it serves (the level 2
  // sounds the day she holds level 1; her decks bring in sound cards by that
  // rule, SOUND_OPENS), and the path opens it once she has begun the set
  // before it, so the track shows one way forward. Anything she does with a
  // set's sounds anywhere (a card, a word) shows it as hers at once.
  const bySound = soundEvidence(input.decks ?? [], memory)
  const soundShown = soundMemories(bySound, memory, now)
  const soundIndex = (step: PathStep) => Number(step.id.slice('sounds-'.length)) - 1
  const soundLevel = (step: PathStep) => SOUND_SETS[soundIndex(step)]?.level ?? 1
  const soundSteps = trackSteps('sounds')
  // Anything she has done with a set's sounds: tried in a word, played, or on a card.
  const begun = (step: PathStep) => wasPlayed(step.id) || ((step.config.letters as string[]) ?? []).some((l) => (bySound[l] ?? []).some((k) => memory[k]?.reps))
  const sounds = setSteps(soundSteps, (step) => (step.config.letters as string[]) ?? [], soundShown, {
    knows: (known) => `Knows ${list(known)}`,
    // Words she has tried with the sound in them that do not prove it yet: what a parent watched her do.
    met: (letters) => {
      const tried = (l: string) => (bySound[l] ?? []).filter((k) => k.startsWith('word:') && memory[k]?.reps).map((k) => k.slice('word:'.length))
      const seen = letters.filter((l) => tried(l).length)
      const words = [...new Set(seen.flatMap(tried))]
      return seen.length ? `Met ${list(seen)} in ${few(words, 3)}, not sure yet` : null
    },
    // One way forward: a set opens once its blending level is near and she
    // has begun the set before it, so a child who has done nothing sees one
    // open set, not four.
    opens: (step) => begun(step) || (soundLevel(step) <= level + 1 && (soundIndex(step) === 0 || begun(soundSteps[soundIndex(step) - 1]!))),
    lock: (step, prev) => {
      if (soundLevel(step) > level + 1) {
        const u = levelUnlock(memory, soundLevel(step) - 1, now)
        return { unlockHint: levelHint(u), unlock: u }
      }
      return { unlockHint: `Opens once she has begun ${list(prev)}`, unlock: { kind: 'set', after: prev } }
    },
  }, now, wasPlayed)

  // Little books: a book opens when she holds its blending level and has met
  // its sight words in a sight game or on a card, so every word in it is
  // one she has a way to read. A book she has already read stays open.
  const books: StepStanding[] = trackSteps('books').map((step) => {
    const bookLevel = Number(step.config.level)
    const unmet = bookSightWords(step.config as any).filter((w) => !evidence(w).length)
    if (!wasPlayed(step.id) && (bookLevel > level || unmet.length)) {
      return { step, state: 'locked' as const, progress: null, fraction: 0, ...bookLock(bookLevel > level ? levelUnlock(memory, bookLevel, now) : undefined, unmet) }
    }
    const reads = Array.isArray(carryOf(played, step.id)?.times) ? carryOf(played, step.id)!.times.length : 0
    return {
      step,
      state: reads >= BOOK_READS ? 'done' as const : reads || wasPlayed(step.id) ? 'playing' as const : 'open' as const,
      progress: progressOf(step),
      fraction: Math.min(1, reads / BOOK_READS),
    }
  })

  // A step the parent took off her path is neither shown nor offered.
  const shown = (steps: StepStanding[]) => steps.filter((s) => !played[s.step.id]?.hidden)
  const byTrack: Record<PathTrackId, StepStanding[]> = { sounds: shown(sounds), blend: [blend], families: shown([families]), sight: shown(sight), books: shown(books) }
  // What her open books still need on sight: a book at her level waits only
  // on the sight words she has not met.
  const bookNeeds = byTrack.books.flatMap((b) => (bookParts(b.unlock).level ? [] : bookParts(b.unlock).words))
  const picked = nextStep({ memory, now, blend, sounds: byTrack.sounds, sight: byTrack.sight, books: byTrack.books, families: byTrack.families[0], familiesAt: played.families?.at, evidence, bookNeeds })
  return {
    tracks: READING_PATH.map((track) => ({ track, steps: byTrack[track.id] })),
    next: picked?.step ?? null,
    why: picked?.why ?? null,
  }
}

/**
 * A track of small sets (sight words, letter sounds): each set opens once
 * she knows half the set before it (openSets, the rule her decks follow
 * too), or by its own condition (`say.opens`), and says what she knows and
 * is learning in it. `evidence` is every memory she has of one member, from
 * any game. A set she already has a member of is never shown locked: she
 * is learning it, whatever its place in the order.
 */
function setSteps(
  steps: PathStep[],
  members: (step: PathStep) => string[],
  evidence: Evidence,
  say: {
    knows: (known: string[]) => string
    lock: (step: PathStep, prev: string[]) => Pick<StepStanding, 'unlockHint' | 'unlock'>
    met?: (members: string[]) => string | null
    opens?: (step: PathStep) => boolean
  },
  now: number,
  wasPlayed: (id: string) => boolean,
): StepStanding[] {
  const knows = (w: string) => evidence(w).some((m) => knowsOnSight(m, now))
  const open = openSets(steps.map(members), knows, say.opens && steps.map(say.opens))
  return steps.map((step, i) => {
    const words = members(step)
    const metWords = words.filter((w) => evidence(w).length > 0)
    const known = words.filter(knows)
    if (!open[i] && !metWords.length) return { step, state: 'locked' as const, progress: null, fraction: 0, ...say.lock(step, i ? members(steps[i - 1]!) : []) }
    const learning = metWords.filter((w) => !known.includes(w))
    const progress = known.length
      ? [say.knows(known), learning.length ? `learning ${list(learning)}` : ''].filter(Boolean).join(' · ')
      : metWords.length ? `Learning ${list(metWords)}` : say.met?.(words) ?? null
    return {
      step,
      state: known.length === words.length ? 'done' as const : metWords.length || wasPlayed(step.id) || progress ? 'playing' as const : 'open' as const,
      progress,
      fraction: (known.length + metWords.length) / (2 * words.length),
      ...(known.length ? { known } : {}),
    }
  })
}

/** Half a day: what she did in it counts as today's practice, wherever the family's clock is. */
const TODAY_MS = 12 * 60 * 60 * 1000

/**
 * What to hand her next, and why. Reading is sounding out, so the phonics
 * steps come first and a sight set never pulls her away from them: a book
 * she has just earned (the reward for sounding out); Blending when one more
 * sitting of it reaches the next level; the sounds blending needs next
 * (a set she mostly knows excepted), until she has practised them today; then Blending, first
 * every day while she holds no level; then word families the day they
 * open, and once she has blended today, until she has played them today; a
 * sight set only when her books need its words or she has already blended
 * today; otherwise Blending again, the daily practice.
 */
function nextStep(p: {
  memory: Record<string, ItemMemory>
  now: number
  blend: StepStanding
  sounds: StepStanding[]
  sight: StepStanding[]
  books: StepStanding[]
  families: StepStanding | undefined
  /** When her families row last changed: playing it saves its carry. */
  familiesAt: number | undefined
  evidence: Evidence
  bookNeeds: string[]
}): { step: StepStanding; why: string } | null {
  const { memory, now, blend, evidence } = p
  const level = blend.level ?? 0
  const today = (keys: string[]) => keys.some((k) => (memory[k]?.last ?? 0) > now - TODAY_MS)
  const blendedToday = Object.values(memory).some((m) => m.item.startsWith('word:') && m.last > now - TODAY_MS)
  const soundKeys = (s: StepStanding) => ((s.step.config.letters as string[]) ?? []).map((l) => itemKey('sound', GRAPHEMES[l] ?? l))
  const levelWords = level ? `level ${level}` : 'level 1'

  // A book she has just earned is the reward for the sounding out: it goes first.
  const book = p.books.find((s) => s.state === 'open')
  if (level && book) return { step: book, why: 'A book she can read herself now' }
  // The first set she is still learning among those her next blending level needs.
  const setLevel = (s: StepStanding) => SOUND_SETS[Number(s.step.id.slice('sounds-'.length)) - 1]?.level ?? 1
  // Blending comes before the sounds when one more sitting of it reaches the
  // next level (the words she read right once count on another day), since
  // a level opens more of her path than any sound set does.
  const t = blend.toward
  // Read right once in an earlier sitting (half a day ago or more), so reading them now counts.
  const ripe = t ? blendedWords(memory).filter((w) => w.level >= t.level && !holds(w.memory, now) && w.memory.streak > 0 && w.memory.last <= now - TODAY_MS).length : 0
  if (t && t.held < t.needed && ripe >= t.needed - t.held) {
    return { step: blend, why: `Words she read right once count when she reads them again, and that will ${levelReach(t.level, blend.rung ?? 1)}` }
  }
  // A set she mostly knows (3 of 4) has done its work for now: she meets its last sound in Blending.
  const mostly = (s: StepStanding) => (s.known?.length ?? 0) >= 0.75 * ((s.step.config.letters as string[]) ?? []).length
  const sounds = p.sounds.find((s) => (s.state === 'open' || s.state === 'playing') && setLevel(s) <= level + 1 && !mostly(s))
  if (sounds && !today(soundKeys(sounds))) {
    return { step: sounds, why: level ? 'The sounds her next blending words use' : 'The sounds come first: blending joins them into words' }
  }
  if (blend.state === 'open' || (!level && !blendedToday)) {
    return { step: blend, why: level ? 'Sounding out is the daily practice' : `Sounding out comes first while she finds ${levelWords}` }
  }
  if (p.families?.state === 'open') return { step: p.families, why: 'She reads her first words, so changing their first sound comes next' }
  if (blendedToday && p.families?.state === 'playing' && !((p.familiesAt ?? 0) > now - TODAY_MS)) {
    return { step: p.families, why: 'She has blended today; changing the first sound makes new words from ones she reads' }
  }
  const needed = p.bookNeeds.length ? p.sight.find((s) => s.state !== 'locked' && s.state !== 'done' && ((s.step.config.words as string[]) ?? []).some((w) => p.bookNeeds.includes(w))) : undefined
  if (needed) return { step: needed, why: `Her next books use ${andList(((needed.step.config.words as string[]) ?? []).filter((w) => p.bookNeeds.includes(w)))}` }
  if (blendedToday) {
    const fresh = p.sight.find((s) => s.state === 'open')
    const due = p.sight.find((s) => s.state === 'playing' && ((s.step.config.words as string[]) ?? []).some((w) => evidence(w).some((m) => m.due <= now)))
    const sight = due ?? fresh
    if (sight) return { step: sight, why: 'She has blended today; a few words by sight to go with it' }
  }
  if (blend.state !== 'done') return { step: blend, why: level ? 'Sounding out is the daily practice' : `Sounding out comes first while she finds ${levelWords}` }
  const playing = [...p.books, ...p.sight, ...(p.families ? [p.families] : [])].find((s) => s.state === 'playing')
  return playing ? { step: playing, why: 'Where she left off' } : null
}

const isPlacement = (p: unknown): p is Placement => !!p && typeof p === 'object' && Number.isInteger((p as Placement).level) && typeof (p as Placement).at === 'number'

/** The fields of a synced artifact row pathPlayed reads. Both apps' rows fit. */
export interface PathRow {
  _id: string
  childId?: string
  kind?: string
  data?: { path?: unknown; config?: unknown; hidden?: unknown; placed?: unknown } | null
  carry?: unknown
  client_id?: string
  deleted?: boolean
  updatedAt?: number
}

/**
 * pathStanding's `played`: each step's row for this child, found by its
 * `data.path` whichever device made it. A step can briefly have two rows
 * (this device's early copy and one the server made elsewhere, by the agent
 * or another device); the one with her progress wins, then the server's
 * (its id is no longer the client id), then the newest, so both apps show
 * her on the same rung and what she plays logs where it is kept.
 */
export function pathPlayed(artifacts: Record<string, PathRow | undefined>, childId: string | undefined): Record<string, PlayedStep & { artifactId: string }> {
  const rank = (a: PathRow) => [(a.carry !== undefined ? 2 : 0) + (a._id !== a.client_id ? 1 : 0), a.updatedAt ?? 0] as const
  const better = (a: PathRow, b: PathRow) => rank(a)[0] - rank(b)[0] || rank(a)[1] - rank(b)[1]
  const rows: Record<string, PathRow> = {}
  for (const a of Object.values(artifacts)) {
    const step = a?.data?.path
    if (!a || a.deleted || a.childId !== childId || typeof step !== 'string' || (a.kind && a.kind !== 'activity')) continue
    if (!rows[step] || better(a, rows[step]!) > 0) rows[step] = a
  }
  // The setup changed for her (a level the parent chose, a book rewritten
  // for her) rides along, and so does a step taken off her path.
  const config = (a: PathRow) => (a.data?.config && typeof a.data.config === 'object' ? { config: a.data.config as Record<string, unknown> } : {})
  return Object.fromEntries(Object.entries(rows).map(([step, a]) => [step, {
    carry: a.carry, artifactId: a._id, ...config(a), ...(a.data?.hidden === true ? { hidden: true } : {}), ...(a.updatedAt ? { at: a.updatedAt } : {}),
    ...(isPlacement(a.data?.placed) ? { placed: a.data.placed } : {}),
  }]))
}
